import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
  Typography,
  FormControlLabel,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import api from '../../services/api';
import { useHelpContent } from '../../utils/HelpUtils';

interface DuplicateDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newName: string, selectedSubcases: string[]) => void;
  documentName: string;
  subcases: string[];
  existingDocumentNames: string[];
  documentId: string;
  subCategories: string[];
}

const DuplicateDocumentDialog: React.FC<DuplicateDocumentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  documentName,
  subcases,
  existingDocumentNames,
  documentId,
  subCategories,
}) => {
  const [newName, setNewName] = useState(`${documentName} - Copy`);
  const [selectedSubcases, setSelectedSubcases] = useState<string[]>([]);
  const [nameError, setNameError] = useState('');
  const [groups, setGroups] = useState<{ name: string; subcases: string[] }[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  /** Subcase group chips (multi-select); merging adds/removes subcases in `selectedSubcases`. */
  const [selectedGroupNames, setSelectedGroupNames] = useState<string[]>([]);
  /** Snapshot when first group chip is turned on; restored when last group chip is turned off. */
  const subcasesBaselineBeforeGroupsRef = useRef<string[] | null>(null);
  const { getTooltipTextForState } = useHelpContent();

  const subcasesUnionForGroupNames = (names: string[]) => {
    const acc = new Set<string>();
    names.forEach(n => {
      const g = groups.find(x => x.name === n);
      g?.subcases.forEach(s => acc.add(s));
    });
    return acc;
  };

  useEffect(() => {
    if (documentName) {
      let baseName = `${documentName} - Copy`;
      let counter = 1;
      let newName = baseName;
      
      // Keep appending numbers until we find a unique name
      while (existingDocumentNames.includes(newName)) {
        newName = `${baseName} (${counter})`;
        counter++;
      }
      
      setNewName(newName);
    }
  }, [documentName, existingDocumentNames]);

  useEffect(() => {
    if (open && documentId) {
      setLoadingGroups(true);
      api.get('/documents/subcase-groups', { params: { documentId } })
        .then(res => {
          const groupsMap = res.data;
          const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
            name,
            subcases: subcases as string[],
          }));
          setGroups(groupsArray);
        })
        .catch(() => setGroups([]))
        .finally(() => setLoadingGroups(false));
    }
  }, [open, documentId]);

  useEffect(() => {
    if (open) {
      setSelectedSubcases([...subcases]);
      setSelectedGroupNames([]);
      subcasesBaselineBeforeGroupsRef.current = null;
    } else {
      setSelectedSubcases([]);
      setNameError('');
      setSelectedGroupNames([]);
      subcasesBaselineBeforeGroupsRef.current = null;
    }
  }, [open, subcases]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewName(value);
    
    if (!value.trim()) {
      setNameError('Document name cannot be empty');
    } else if (existingDocumentNames.includes(value.trim())) {
      setNameError('A document with this name already exists');
    } else if (subCategories.includes(value.trim())) {
      setNameError('Document contains a sub category with this name');
    } else {
      setNameError('');
    }
  };

  const handleSubcaseToggle = (subcase: string) => {
    setSelectedSubcases(prev => {
      if (prev.includes(subcase)) {
        return prev.filter(s => s !== subcase);
      } else {
        return [...prev, subcase];
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    subcasesBaselineBeforeGroupsRef.current = null;
    if (checked) {
      setSelectedSubcases([...subcases]);
    } else {
      setSelectedSubcases([]);
    }
  };

  const allDocumentSubcasesSelected = (selected: string[]) =>
    subcases.length > 0 &&
    selected.length === subcases.length &&
    subcases.every(s => selected.includes(s));

  const handleGroupSelect = (groupName: string) => {
    const group = groups.find(g => g.name === groupName);
    if (!group) return;

    setSelectedGroupNames(prevNames => {
      const turningOff = prevNames.includes(groupName);
      const nextNames = turningOff
        ? prevNames.filter(n => n !== groupName)
        : [...prevNames, groupName];

      setSelectedSubcases(prevSub => {
        if (!turningOff) {
          if (prevNames.length === 0) {
            subcasesBaselineBeforeGroupsRef.current = [...prevSub];
          }
          // From "whole document" + no groups yet → narrow to this group only (merge adds later).
          if (allDocumentSubcasesSelected(prevSub) && prevNames.length === 0) {
            return [...group.subcases];
          }
          const merged = [...prevSub];
          for (const s of group.subcases) {
            if (merged.indexOf(s) === -1) merged.push(s);
          }
          return merged;
        }
        const cover = subcasesUnionForGroupNames(nextNames);
        const filtered = prevSub.filter(s => !group.subcases.includes(s) || cover.has(s));
        if (nextNames.length === 0) {
          if (subcasesBaselineBeforeGroupsRef.current !== null) {
            const restored = [...subcasesBaselineBeforeGroupsRef.current];
            subcasesBaselineBeforeGroupsRef.current = null;
            return restored;
          }
          return filtered.length === 0 ? [...subcases] : filtered;
        }
        return filtered;
      });

      return nextNames;
    });
  };

  const handleConfirm = () => {
    if (newName.trim() && !nameError) {
      onConfirm(newName.trim(), selectedSubcases);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicate Document</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Tooltip title={getTooltipTextForState('duplicate-document-select-all', true)} placement="top">
            <TextField
              autoFocus
              margin="dense"
              label="New Document Name"
              fullWidth
              value={newName}
              onChange={handleNameChange}
              error={!!nameError}
              helperText={nameError}
            />
          </Tooltip>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Select Subcases to Include (Optional)</Typography>
          {loadingGroups ? (
            <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading groups...</Typography>
            </Box>
          ) : groups.length > 0 && (
            <Box sx={{ my: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {groups.map(group => (
                <Tooltip key={group.name} title={getTooltipTextForState('duplicate-category-subcase-groups', true)} placement="top">
                  <Chip
                    label={group.name}
                    color={selectedGroupNames.includes(group.name) ? 'primary' : 'default'}
                    onClick={() => handleGroupSelect(group.name)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
          <Tooltip title={getTooltipTextForState('duplicate-document-select-all', true)} placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedSubcases.length === subcases.length}
                  indeterminate={selectedSubcases.length > 0 && selectedSubcases.length < subcases.length}
                  onChange={(e) => {
                    handleSelectAll(e.target.checked);
                    setSelectedGroupNames([]);
                  }}
                />
              }
              label="Select All"
            />
          </Tooltip>
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {subcases.map((subcase) => (
              <ListItem key={subcase} disablePadding>
                <ListItemButton onClick={() => handleSubcaseToggle(subcase)}>
                  <Checkbox
                    edge="start"
                    checked={selectedSubcases.includes(subcase)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText primary={subcase} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
          <Button onClick={onClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getTooltipTextForState('duplicate', true)} placement="top">
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            disabled={!newName.trim() || !!nameError}
          >
            Duplicate
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateDocumentDialog; 